import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dealerAPI } from '../../services/api';

// Async thunk for fetching cities
export const fetchCities = createAsyncThunk(
  'dealer/fetchCities',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dealerAPI.getCities();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Şehir verileri yüklenirken hata oluştu');
    }
  }
);

// Async thunk for fetching districts
export const fetchDistricts = createAsyncThunk(
  'dealer/fetchDistricts',
  async (cityId, { rejectWithValue }) => {
    try {
      const response = await dealerAPI.getDistricts(cityId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'İlçe verileri yüklenirken hata oluştu');
    }
  }
);

// Async thunk for creating new contact
export const createContact = createAsyncThunk(
  'dealer/createContact',
  async (contactData, { rejectWithValue }) => {
    try {
      const response = await dealerAPI.createContact(contactData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'İletişim bilgisi oluşturulurken hata oluştu');
    }
  }
);

// Async thunk for requesting SMS
export const requestSMS = createAsyncThunk(
  'dealer/requestSMS',
  async (phone, { rejectWithValue }) => {
    try {
      const response = await dealerAPI.requestSMS(phone);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'SMS isteği hatası');
    }
  }
);

// Async thunk for verifying SMS code
export const verifySMS = createAsyncThunk(
  'dealer/verifySMS',
  async ({ phone, smsCode }, { rejectWithValue }) => {
    try {
      const response = await dealerAPI.verifySMS(phone, smsCode);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'SMS doğrulama hatası');
    }
  }
);

const dealerSlice = createSlice({
  name: 'dealer',
  initialState: {
    cities: [],
    districts: [],
    selectedCityId: null,
    selectedDistrictId: null,
    contactLoading: false,
    contactError: null,
    contactSuccess: false,
    smsRequestLoading: false,
    smsRequestError: null,
    smsRequestSuccess: false,
    smsVerificationLoading: false,
    smsVerificationError: null,
    smsVerificationSuccess: false,
    citiesLoading: false,
    citiesError: null,
    districtsLoading: false,
    districtsError: null,
  },
  reducers: {
    setSelectedCity: (state, action) => {
      state.selectedCityId = action.payload;
      state.selectedDistrictId = null; // Reset district when city changes
      state.districts = []; // Clear districts
    },
    setSelectedDistrict: (state, action) => {
      state.selectedDistrictId = action.payload;
    },
    clearContactState: (state) => {
      state.contactLoading = false;
      state.contactError = null;
      state.contactSuccess = false;
    },
    clearSMSRequestState: (state) => {
      state.smsRequestLoading = false;
      state.smsRequestError = null;
      state.smsRequestSuccess = false;
    },
    clearSMSVerificationState: (state) => {
      state.smsVerificationLoading = false;
      state.smsVerificationError = null;
      state.smsVerificationSuccess = false;
    },
    resetForm: (state) => {
      state.selectedCityId = null;
      state.selectedDistrictId = null;
      state.contactLoading = false;
      state.contactError = null;
      state.contactSuccess = false;
      state.smsRequestLoading = false;
      state.smsRequestError = null;
      state.smsRequestSuccess = false;
      state.smsVerificationLoading = false;
      state.smsVerificationError = null;
      state.smsVerificationSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch cities
      .addCase(fetchCities.pending, (state) => {
        state.citiesLoading = true;
        state.citiesError = null;
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.citiesLoading = false;
        state.cities = action.payload.data || [];
        state.citiesError = null;
      })
      .addCase(fetchCities.rejected, (state, action) => {
        state.citiesLoading = false;
        state.citiesError = action.payload;
      })
      // Fetch districts
      .addCase(fetchDistricts.pending, (state) => {
        state.districtsLoading = true;
        state.districtsError = null;
      })
      .addCase(fetchDistricts.fulfilled, (state, action) => {
        state.districtsLoading = false;
        state.districts = action.payload.data || [];
        state.districtsError = null;
      })
      .addCase(fetchDistricts.rejected, (state, action) => {
        state.districtsLoading = false;
        state.districtsError = action.payload;
      })
      // Create contact
      .addCase(createContact.pending, (state) => {
        state.contactLoading = true;
        state.contactError = null;
        state.contactSuccess = false;
      })
                  .addCase(createContact.fulfilled, (state, action) => {
               state.contactLoading = false;
               state.contactSuccess = action.payload?.data === true && (action.payload?.statu === 200 || action.payload?.status === 200);
               state.contactError = !state.contactSuccess ? (action.payload?.exceptions?.[0] || 'İşlem tamamlanamadı') : null;
             })
      .addCase(createContact.rejected, (state, action) => {
        state.contactLoading = false;
        // Extract error message from payload
        if (action.payload && typeof action.payload === 'object') {
          if (action.payload.title) {
            state.contactError = action.payload.title;
          } else if (action.payload.message) {
            state.contactError = action.payload.message;
          } else if (action.payload.errors) {
            state.contactError = Object.values(action.payload.errors).join(', ');
          } else {
            state.contactError = 'İletişim oluşturma başarısız oldu';
          }
        } else {
          state.contactError = action.payload || 'İletişim oluşturma başarısız oldu';
        }
        state.contactSuccess = false;
      })
      // Request SMS
      .addCase(requestSMS.pending, (state) => {
        state.smsRequestLoading = true;
        state.smsRequestError = null;
        state.smsRequestSuccess = false;
      })
      .addCase(requestSMS.fulfilled, (state, action) => {
        state.smsRequestLoading = false;
        state.smsRequestSuccess = true;
        state.smsRequestError = null;
      })
      .addCase(requestSMS.rejected, (state, action) => {
        state.smsRequestLoading = false;
        // Extract error message from payload
        if (action.payload && typeof action.payload === 'object') {
          if (action.payload.title) {
            state.smsRequestError = action.payload.title;
          } else if (action.payload.message) {
            state.smsRequestError = action.payload.message;
          } else if (action.payload.errors) {
            state.smsRequestError = Object.values(action.payload.errors).join(', ');
          } else {
            state.smsRequestError = 'SMS isteği başarısız oldu';
          }
        } else {
          state.smsRequestError = action.payload || 'SMS isteği başarısız oldu';
        }
        state.smsRequestSuccess = false;
      })
      // Verify SMS
      .addCase(verifySMS.pending, (state) => {
        state.smsVerificationLoading = true;
        state.smsVerificationError = null;
        state.smsVerificationSuccess = false;
      })
      .addCase(verifySMS.fulfilled, (state, action) => {
        state.smsVerificationLoading = false;
        state.smsVerificationSuccess = action.payload.data === true;
        state.smsVerificationError = action.payload.exceptions?.[0] || null;
      })
      .addCase(verifySMS.rejected, (state, action) => {
        state.smsVerificationLoading = false;
        state.smsVerificationError = action.payload;
        state.smsVerificationSuccess = false;
      });
  },
});

export const {
  setSelectedCity,
  setSelectedDistrict,
  clearContactState,
  clearSMSRequestState,
  clearSMSVerificationState,
  resetForm,
} = dealerSlice.actions;

// Selectors
export const selectCities = (state) => state.dealer.cities;
export const selectDistricts = (state) => state.dealer.districts;
export const selectSelectedCityId = (state) => state.dealer.selectedCityId;
export const selectSelectedDistrictId = (state) => state.dealer.selectedDistrictId;
export const selectContactLoading = (state) => state.dealer.contactLoading;
export const selectContactError = (state) => state.dealer.contactError;
export const selectContactSuccess = (state) => state.dealer.contactSuccess;
export const selectSMSRequestLoading = (state) => state.dealer.smsRequestLoading;
export const selectSMSRequestError = (state) => state.dealer.smsRequestError;
export const selectSMSRequestSuccess = (state) => state.dealer.smsRequestSuccess;
export const selectSMSVerificationLoading = (state) => state.dealer.smsVerificationLoading;
export const selectSMSVerificationError = (state) => state.dealer.smsVerificationError;
export const selectSMSVerificationSuccess = (state) => state.dealer.smsVerificationSuccess;
export const selectCitiesLoading = (state) => state.dealer.citiesLoading;
export const selectCitiesError = (state) => state.dealer.citiesError;
export const selectDistrictsLoading = (state) => state.dealer.districtsLoading;
export const selectDistrictsError = (state) => state.dealer.districtsError;

export default dealerSlice.reducer; 